import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { LoadingSpinner } from '../../../components/LoadingSpinner';
import {
  AdminQueryKeys,
  ALERT_TYPES,
  deleteAlertRule,
  listAlertRules,
  upsertAlertRule,
  type AdminAlertRuleResponse,
  type UpsertAlertRuleBody,
} from '../api';
import { EmailAlertAdminTabs } from '../components/EmailAlertAdminTabs';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const EMPTY: UpsertAlertRuleBody = {
  alertType: 'NearUsageLimit',
  targetingMode: 'AllTenants',
  tenantIds: [],
  thresholdsPercent: [80, 100],
  sendDayOfWeek: 'Monday',
  isEnabled: true,
};

/** Admin page: manage alert rules (who/when an alert fires). */
export function AlertRulesPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: AdminQueryKeys.alertRules(),
    queryFn: ({ signal }) => listAlertRules(signal),
  });

  const [form, setForm] = useState<UpsertAlertRuleBody>(EMPTY);
  const [tenantsText, setTenantsText] = useState('');
  const [thresholdsText, setThresholdsText] = useState('80, 100');
  const [error, setError] = useState<string | null>(null);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: AdminQueryKeys.alertRules() });
  };
  const upsert = useMutation({
    mutationFn: upsertAlertRule,
    onSuccess: () => { setError(null); invalidate(); },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'No se pudo guardar.'),
  });
  const remove = useMutation({ mutationFn: deleteAlertRule, onSuccess: invalidate });

  const isNearLimit = form.alertType === 'NearUsageLimit';

  const submit = (): void => {
    const body: UpsertAlertRuleBody = {
      ...form,
      tenantIds: form.targetingMode === 'SelectedTenants'
        ? tenantsText.split(',').map((t) => t.trim()).filter((t) => t !== '')
        : [],
      thresholdsPercent: isNearLimit
        ? thresholdsText.split(',').map((t) => Number(t.trim())).filter((n) => Number.isFinite(n))
        : [],
      sendDayOfWeek: isNearLimit ? null : (form.sendDayOfWeek ?? null),
    };
    upsert.mutate(body);
  };

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">Alertas por email</h1>
        <p className="mt-1 text-sm text-slate-500">Reglas: a qué tenants y cuándo se envía cada alerta.</p>
      </header>

      <EmailAlertAdminTabs />

      {isLoading ? (
        <div className="flex justify-center py-8"><LoadingSpinner /></div>
      ) : isError || data === undefined ? (
        <div className="card border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">No se pudieron cargar las reglas.</div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs font-medium uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3">Destino</th>
                <th className="px-4 py-3">Umbrales / Día</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-testid="rules-rows">
              {data.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">Sin reglas configuradas.</td></tr>
              ) : (
                data.map((r: AdminAlertRuleResponse) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-700">{r.alertType}</td>
                    <td className="px-4 py-3 text-slate-700">
                      {r.targetingMode === 'AllTenants' ? 'Todos' : `${r.tenantIds.length} tenant(s)`}
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {r.alertType === 'NearUsageLimit' ? r.thresholdsPercent.map((t) => `${t}%`).join(', ') : (r.sendDayOfWeek ?? '—')}
                    </td>
                    <td className="px-4 py-3">{r.isEnabled ? 'Activa' : 'Inactiva'}</td>
                    <td className="px-4 py-3 text-right">
                      <button type="button" onClick={() => remove.mutate(r.id)} disabled={remove.isPending}
                        className="text-xs font-medium text-rose-600 hover:underline disabled:opacity-50">
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="card space-y-3 p-4">
        <h2 className="text-base font-semibold text-slate-900">Crear / actualizar regla</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-sm text-slate-700">
            Tipo de alerta
            <select className="input mt-1" value={form.alertType} onChange={(e) => setForm({ ...form, alertType: e.target.value })} data-testid="rule-type">
              {ALERT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label className="text-sm text-slate-700">
            Destino
            <select className="input mt-1" value={form.targetingMode} onChange={(e) => setForm({ ...form, targetingMode: e.target.value })}>
              <option value="AllTenants">Todos los tenants</option>
              <option value="SelectedTenants">Tenants seleccionados</option>
            </select>
          </label>
          {form.targetingMode === 'SelectedTenants' ? (
            <input className="input sm:col-span-2" placeholder="tenant_aaa111, tenant_bbb222" value={tenantsText}
              onChange={(e) => setTenantsText(e.target.value)} />
          ) : null}
          {isNearLimit ? (
            <input className="input" placeholder="Umbrales % (80, 100)" value={thresholdsText}
              onChange={(e) => setThresholdsText(e.target.value)} data-testid="rule-thresholds" />
          ) : (
            <label className="text-sm text-slate-700">
              Día de envío
              <select className="input mt-1" value={form.sendDayOfWeek ?? 'Monday'} onChange={(e) => setForm({ ...form, sendDayOfWeek: e.target.value })}>
                {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
          )}
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.isEnabled} onChange={(e) => setForm({ ...form, isEnabled: e.target.checked })} />
            Activa (desactiva cualquier otra regla del mismo tipo)
          </label>
        </div>
        {error !== null ? <p className="text-xs text-rose-700">{error}</p> : null}
        <button type="button" onClick={submit} disabled={upsert.isPending} className="btn-primary" data-testid="rule-submit">
          {upsert.isPending ? 'Guardando…' : 'Guardar regla'}
        </button>
      </div>
    </div>
  );
}
